// TMDB API client — mirrors the Android app's TmdbApi endpoints and the
// error mapping in BrowseViewModels.toUiMessage() so the web platform behaves
// the same way as the TV app.
import { apiBaseUrl, config, isProxyMode } from "../config";
import { fetchLibraryIndex } from "./library-index";
import {
  dedupeCards,
  parseSearchQuery,
  rankSearchResults,
  searchMatch,
  yearOfCard,
} from "./search-rank";

const DEFAULT_HEADERS = { Accept: "application/json" };

export class TmdbError extends Error {
  constructor(message, code) {
    super(message);
    this.name = "TmdbError";
    this.code = code;
  }
}

export class TmdbNotConfiguredError extends Error {
  constructor(message) {
    super(message || "TMDB is not configured yet.");
    this.name = "TmdbNotConfiguredError";
  }
}

// Maps failures to the same friendly messages the Android app shows.
function toUiMessage(error, status) {
  if (error instanceof TmdbNotConfiguredError) return error.message;
  if (error instanceof TypeError && error.message === "Failed to fetch") {
    return "Network unavailable. Check the connection and try again.";
  }
  switch (status) {
    case 401:
    case 403:
      return "TMDB authentication failed. Check the configured token or proxy.";
    case 404:
      return "This title could not be found.";
    case 429:
      return "TMDB is receiving too many requests. Please wait and retry.";
    case 502:
    case 503:
    case 504:
      return "The metadata service is temporarily unavailable. Try again.";
    default:
      if (status) return `TMDB returned an error (${status}). Try again.`;
      return error?.message || "Something went wrong. Try again.";
  }
}

async function parseError(response) {
  let detail = "";
  try {
    const body = await response.json();
    detail = body?.error || body?.status_message || "";
  } catch {
    /* non-JSON error body */
  }
  return detail;
}

async function apiFetch(path, params = {}, signal) {
  const query = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== "") query.set(key, String(value));
  });
  const qs = query.toString();
  const url = `${apiBaseUrl}${path}${qs ? `?${qs}` : ""}`;

  const headers = { ...DEFAULT_HEADERS };
  if (!isProxyMode) headers.Authorization = `Bearer ${config.tmdbToken}`;

  let response;
  try {
    response = await fetch(url, { headers, signal });
  } catch (error) {
    throw new TmdbError(toUiMessage(error, null), 0);
  }

  if (response.status === 500 || response.status === 501) {
    const detail = await parseError(response);
    if (/not configured|proxy/i.test(detail)) {
      throw new TmdbNotConfiguredError(
        "TMDB is not connected yet. Set a TMDB token or configure the metadata proxy on this site.",
      );
    }
  }

  if (!response.ok) {
    const ctype = response.headers.get("content-type") || "";
    // In proxy mode, a 404 that returns no/HTML body (rather than TMDB JSON)
    // means the same-origin /api/tmdb proxy is not deployed — explain configuration.
    const notJsonBody = ctype.length === 0 || ctype.includes("text/html") || ctype.includes("text/plain");
    if (isProxyMode && response.status === 404 && notJsonBody) {
      throw new TmdbNotConfiguredError(
        "TMDB is not connected yet. Add a TMDB token to public/config.js (direct mode) or deploy this site with the /api/tmdb metadata proxy — see web/README.md.",
      );
    }
    const detail = await parseError(response);
    throw new TmdbError(toUiMessage(new Error(detail), response.status), response.status);
  }
  return response.json();
}

// ---- Endpoints (same paths as TmdbApi.kt) ----

export const tmdb = {
  trending: (page = 1) => apiFetch("trending/all/day", { page }),
  trendingMovies: (page = 1) => apiFetch("trending/movie/week", { page }),
  trendingTv: (page = 1) => apiFetch("trending/tv/week", { page }),
  popularMovies: (page = 1) => apiFetch("movie/popular", { page }),
  nowPlayingMovies: (page = 1) => apiFetch("movie/now_playing", { page }),
  topRatedMovies: (page = 1) => apiFetch("movie/top_rated", { page }),
  popularTv: (page = 1) => apiFetch("tv/popular", { page }),
  topRatedTv: (page = 1) => apiFetch("tv/top_rated", { page }),
  onAirTv: (page = 1) => apiFetch("tv/on_the_air", { page }),
  discoverMovies: (opts = {}) =>
    apiFetch("discover/movie", {
      with_original_language: opts.originalLanguage,
      with_genres: opts.genres,
      sort_by: opts.sortBy || "popularity.desc",
      include_adult: opts.includeAdult || false,
      page: opts.page || 1,
    }),
  // Movies currently available on the main Indian OTT platforms (Netflix =
  // 8, Prime Video = 119, Disney+ Hotstar = 337) and released within the
  // last `days` (default 60), newest release date first — the “recent OTT
  // releases” hero feed. Release dates are capped at today, so unreleased
  // titles never appear.
  ottReleasesMovies: (opts = {}) => {
    const days = Number(opts.days || 60);
    const now = new Date();
    const from = new Date(now.getTime() - days * 24 * 60 * 60 * 1000);
    return apiFetch("discover/movie", {
      watch_region: "IN",
      with_watch_providers: "8|119|337",
      "primary_release_date.gte": toISODate(from),
      "primary_release_date.lte": toISODate(now),
      sort_by: "primary_release_date.desc",
      include_adult: false,
      page: opts.page || 1,
    });
  },
  discoverTv: (opts = {}) =>
    apiFetch("discover/tv", {
      with_original_language: opts.originalLanguage,
      sort_by: opts.sortBy || "popularity.desc",
      include_adult: opts.includeAdult || false,
      page: opts.page || 1,
    }),
  searchMulti: (query, page = 1) =>
    apiFetch("search/multi", { query, include_adult: false, page }),
  // Year-scoped searches: search/multi has no year filter, so a query that
  // names a year ("boys 2003") also runs against these two.
  searchMovie: ({ query, year, page = 1 }) =>
    apiFetch("search/movie", { query, include_adult: false, primary_release_year: year, page }),
  searchTv: ({ query, year, page = 1 }) =>
    apiFetch("search/tv", { query, include_adult: false, first_air_date_year: year, page }),
  // Bare detail fetch (no appended recommendations) — used to build a card for
  // a library title that TMDB's search ordering buried.
  movieById: (id) => apiFetch(`movie/${id}`),
  tvById: (id) => apiFetch(`tv/${id}`),
  movieDetails: (id) => apiFetch(`movie/${id}`, { append_to_response: "recommendations" }),
  tvDetails: (id) => apiFetch(`tv/${id}`, { append_to_response: "recommendations" }),
  seasonDetails: (showId, seasonNumber) => apiFetch(`tv/${showId}/season/${seasonNumber}`),
};

// ---- Domain mapping (mirrors RealCatalogRepository) ----

export const ContentType = { MOVIE: "MOVIE", TV_SHOW: "TV_SHOW", EPISODE: "EPISODE" };

const INDIAN_LANGUAGES = new Set(["hi", "ml", "ta", "te", "kn", "bn", "mr", "pa", "gu", "or", "as"]);

// Genre rows shown on the Movies page (TMDB genre ids). The full listing
// per genre is browsable via the Movies page's genre chips.
export const GENRE_BROWSE = [
  { genreId: "28", title: "Action" },
  { genreId: "35", title: "Comedy" },
  { genreId: "27", title: "Horror" },
  { genreId: "10749", title: "Romance" },
  { genreId: "16", title: "Animation" },
  { genreId: "53", title: "Thrillers" },
  { genreId: "878", title: "Sci-Fi" },
  { genreId: "18", title: "Drama" },
];

// True when a YYYY-MM-DD (or longer) date is strictly after today, local time.
// Titles dated in the future are unreleased — there is no file to watch — so
// every browse surface filters them out.
export function isFutureDated(dateStr) {
  if (!dateStr || !/^\d{4}-\d{2}-\d{2}/.test(dateStr)) return false;
  const now = new Date();
  const y = now.getFullYear();
  const m = String(now.getMonth() + 1).padStart(2, "0");
  const d = String(now.getDate()).padStart(2, "0");
  return dateStr.slice(0, 10) > `${y}-${m}-${d}`;
}

function mediaTypeOf(dto) {
  if (dto.media_type === "movie") return ContentType.MOVIE;
  if (dto.media_type === "tv") return ContentType.TV_SHOW;
  return null;
}

function toCard(dto, fallbackType = null) {
  const type = mediaTypeOf(dto) || fallbackType;
  if (!type) return null;
  const title = type === ContentType.MOVIE ? dto.title : dto.name;
  if (!title) return null;
  const date = type === ContentType.MOVIE ? dto.release_date : dto.first_air_date;
  // Unreleased titles (future release / first-air date) are dropped from
  // every list, row, search result and recommendation.
  if (isFutureDated(date)) return null;
  return {
    tmdbId: dto.id,
    contentType: type,
    title,
    overview: dto.overview || "",
    posterPath: dto.poster_path || null,
    backdropPath: dto.backdrop_path || null,
    date,
    rating: dto.vote_average || 0,
    popularity: dto.popularity || 0,
    originalLanguage: dto.original_language || null,
    // List endpoints return genre_ids; used to keep documentaries out of
    // the OTT-releases hero.
    genreIds: dto.genre_ids || (dto.genres || []).map((g) => g.id),
  };
}

// Search ordering (starts-with first, then other-word matches, then
// substrings, with a trailing year matched against release dates) lives in
// ./search-rank.js, where it is unit-tested without a network.

// Exported for admin tooling (search result cards from raw list DTOs).
export function toCards(results, fallbackType = null) {
  return (results || [])
    .map((dto) => toCard(dto, fallbackType))
    .filter(Boolean);
}

function sortRecommendations(cards, originalLanguage) {
  if (!originalLanguage || !INDIAN_LANGUAGES.has(originalLanguage)) return cards;
  return [...cards].sort((a, b) => {
    const score = (c) => {
      if (c.originalLanguage === originalLanguage) return 2;
      if (INDIAN_LANGUAGES.has(c.originalLanguage)) return 1;
      return 0;
    };
    return score(b) - score(a);
  });
}

function toMovie(dto) {
  return {
    tmdbId: dto.id,
    title: dto.title,
    overview: dto.overview || "",
    posterPath: dto.poster_path || null,
    backdropPath: dto.backdrop_path || null,
    releaseDate: dto.release_date || null,
    rating: dto.vote_average || 0,
    runtimeMinutes: dto.runtime ?? null,
    genres: (dto.genres || []).map((g) => ({ id: g.id, name: g.name })),
    originalLanguage: dto.original_language || null,
    recommendations: sortRecommendations(
      toCards(dto.recommendations?.results, ContentType.MOVIE),
      dto.original_language,
    ),
  };
}

function toTvShow(dto) {
  return {
    tmdbId: dto.id,
    title: dto.name,
    overview: dto.overview || "",
    posterPath: dto.poster_path || null,
    backdropPath: dto.backdrop_path || null,
    firstAirDate: dto.first_air_date || null,
    rating: dto.vote_average || 0,
    genres: (dto.genres || []).map((g) => ({ id: g.id, name: g.name })),
    originalLanguage: dto.original_language || null,
    seasons: (dto.seasons || [])
      .filter((s) => s.season_number > 0)
      // A season that hasn't premiered yet has no episodes to watch.
      .filter((s) => !isFutureDated(s.air_date))
      .map((s) => ({
        seasonNumber: s.season_number,
        name: s.name,
        overview: s.overview || "",
        posterPath: s.poster_path || null,
        airDate: s.air_date || null,
        episodeCount: s.episode_count || 0,
        episodes: [],
      })),
    recommendations: toCards(dto.recommendations?.results, ContentType.TV_SHOW),
  };
}

function toSeason(dto, showId, showTitle) {
  return {
    seasonNumber: dto.season_number,
    name: dto.name,
    overview: dto.overview || "",
    posterPath: dto.poster_path || null,
    airDate: dto.air_date || null,
    episodeCount: (dto.episodes || []).length,
    // Un-aired episodes (future air date) have no file on the provider, so
    // they are hidden exactly like unreleased movies.
    episodes: (dto.episodes || [])
      .filter((e) => !isFutureDated(e.air_date))
      .map((e) => ({
        tmdbId: e.id,
        showTmdbId: showId,
        showTitle,
        seasonNumber: e.season_number,
        episodeNumber: e.episode_number,
        title: e.name,
        overview: e.overview || "",
        stillPath: e.still_path || null,
        runtimeMinutes: e.runtime ?? null,
        airDate: e.air_date || null,
      })),
  };
}

// Local YYYY-MM-DD for a Date (hero windows and the future-date filter use
// the same format, so string comparison stays consistent).
function toISODate(date) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

async function safeCards(call, fallbackType) {
  try {
    return toCards(await call(), fallbackType);
  } catch {
    return [];
  }
}

// ---- Search recall helpers ------------------------------------------------

// A year spelled out in the query ("boys 2003") is not something
// search/multi can filter on, and popularity ordering buries small releases,
// so the year runs as a second, year-scoped search whose results are merged
// into the name matches.
async function searchByYear(text, year) {
  const [movies, shows] = await Promise.all([
    safeCards(() => tmdb.searchMovie({ query: text, year }).then((p) => p.results), ContentType.MOVIE),
    safeCards(() => tmdb.searchTv({ query: text, year }).then((p) => p.results), ContentType.TV_SHOW),
  ]);
  return [...movies, ...shows];
}

// Cards for library titles the query names are fetched by id (one small TMDB
// request each, cached below) so they render like any other result: poster,
// year, rating. Without this, a hosted title TMDB ranks on page 90 could never
// appear in the grid no matter how the page-1 results are ordered.
const libraryCardCache = new Map();
const LIBRARY_CARD_TTL_MS = 10 * 60 * 1000;
// A loose query ("a") can name most of the library; hydrate only the best few.
const LIBRARY_MATCH_LIMIT = 8;

async function libraryCardFor(entry) {
  const key = `${entry.contentType}:${entry.tmdbId}`;
  const hit = libraryCardCache.get(key);
  if (hit && Date.now() - hit.at < LIBRARY_CARD_TTL_MS) return hit.card;
  let card = null;
  try {
    const dto =
      entry.contentType === ContentType.MOVIE
        ? await tmdb.movieById(entry.tmdbId)
        : await tmdb.tvById(entry.tmdbId);
    card = toCard(dto, entry.contentType);
  } catch {
    card = null;
  }
  libraryCardCache.set(key, { at: Date.now(), card });
  return card;
}

async function libraryCardsFor(terms, phrase) {
  if (!terms.length) return [];
  const entries = await fetchLibraryIndex();
  const matches = entries
    .map((entry) => ({ entry, m: searchMatch(entry.title, terms, phrase) }))
    // Tier 4 is "one query word appears anywhere" — the loosest tier, which for
    // a one-letter query would drag in half the library for nothing.
    .filter((x) => x.m && x.m.rank <= 3)
    .slice(0, LIBRARY_MATCH_LIMIT);
  const cards = await Promise.all(matches.map((x) => libraryCardFor(x.entry)));
  return cards.filter(Boolean);
}

// ---- Feed composition (mirrors RealCatalogRepository) ----

export const catalog = {
  async homeFeed() {
    const [trending, popularMovies, popularTv, malayalam, tamil, hindi, telugu, topRated, ottReleases, indianOtt] =
      await Promise.all([
        tmdb.trending().then((p) => toCards(p.results)),
        safeCards(() => tmdb.popularMovies().then((p) => p.results), ContentType.MOVIE),
        safeCards(() => tmdb.popularTv().then((p) => p.results), ContentType.TV_SHOW),
        safeCards(() => tmdb.discoverMovies({ originalLanguage: "ml" }).then((p) => p.results), ContentType.MOVIE),
        safeCards(() => tmdb.discoverMovies({ originalLanguage: "ta" }).then((p) => p.results), ContentType.MOVIE),
        safeCards(() => tmdb.discoverMovies({ originalLanguage: "hi" }).then((p) => p.results), ContentType.MOVIE),
        safeCards(() => tmdb.discoverMovies({ originalLanguage: "te" }).then((p) => p.results), ContentType.MOVIE),
        safeCards(() => tmdb.topRatedMovies().then((p) => p.results), ContentType.MOVIE),
        this.recentOttMovies(),
        this.recentIndianOttMovies(),
      ]);

    const rows = [
      { id: "trending", title: "Trending Now", items: trending },
      { id: "indian_ott", title: "New on OTT — Indian Movies", items: indianOtt },
      { id: "popular_movies", title: "Popular Movies", items: popularMovies },
      { id: "popular_tv", title: "Popular TV Shows", items: popularTv },
      { id: "malayalam", title: "Malayalam Movies", items: malayalam },
      { id: "tamil", title: "Tamil Movies", items: tamil },
      { id: "hindi", title: "Hindi Movies", items: hindi },
      { id: "telugu", title: "Telugu Movies", items: telugu },
      { id: "top_rated", title: "Critically Acclaimed", items: topRated },
    ].filter((r) => r.items.length > 0);

    // Hero slideshow: the 5 most recent Indian OTT releases (newest first,
    // backdrop artwork preferred). If the OTT feed is empty (metadata hiccup),
    // fall back to trending movies so the hero never goes blank.
    const slides = heroSlides(
      ottReleases.length > 0 ? ottReleases : trending.filter((c) => c.contentType === ContentType.MOVIE),
    );
    return { slides, rows };
  },

  async movieRows() {
    const [trending, popular, nowPlaying, top, malayalam, tamil, telugu, hindi, kannada, genresMap] = await Promise.all([
      tmdb.trendingMovies().then((p) => toCards(p.results, ContentType.MOVIE)),
      safeCards(() => tmdb.popularMovies().then((p) => p.results), ContentType.MOVIE),
      safeCards(() => tmdb.nowPlayingMovies().then((p) => p.results), ContentType.MOVIE),
      safeCards(() => tmdb.topRatedMovies().then((p) => p.results), ContentType.MOVIE),
      safeCards(() => tmdb.discoverMovies({ originalLanguage: "ml" }).then((p) => p.results), ContentType.MOVIE),
      safeCards(() => tmdb.discoverMovies({ originalLanguage: "ta" }).then((p) => p.results), ContentType.MOVIE),
      safeCards(() => tmdb.discoverMovies({ originalLanguage: "te" }).then((p) => p.results), ContentType.MOVIE),
      safeCards(() => tmdb.discoverMovies({ originalLanguage: "hi" }).then((p) => p.results), ContentType.MOVIE),
      safeCards(() => tmdb.discoverMovies({ originalLanguage: "kn" }).then((p) => p.results), ContentType.MOVIE),
      Promise.all(GENRE_BROWSE.map(async (g) => ({
        ...g,
        items: await safeCards(() => tmdb.discoverMovies({ genres: g.genreId }).then((p) => p.results), ContentType.MOVIE),
      }))),
    ]);
    const rows = [
      { id: "movie_trending", title: "Trending Movies", items: trending },
      { id: "movie_popular", title: "Popular", items: popular },
      { id: "movie_now", title: "Now Playing", items: nowPlaying },
      { id: "movie_top", title: "Top Rated", items: top },
      { id: "movie_malayalam", title: "Malayalam Movies", items: malayalam },
      { id: "movie_tamil", title: "Tamil Movies", items: tamil },
      { id: "movie_telugu", title: "Telugu Movies", items: telugu },
      { id: "movie_hindi", title: "Hindi Movies", items: hindi },
      { id: "movie_kannada", title: "Kannada Movies", items: kannada },
    ];
    // Genre browse rows follow the language rows; each is a slice of its
    // full genre listing (the rest loads when the user opens the genre).
    for (const g of genresMap) rows.push({ id: "movie_genre_" + g.genreId, title: g.title, items: g.items, genreId: g.genreId });
    return rows.filter((r) => r.items.length > 0);
  },

  async tvRows() {
    const [trending, popular, top, onAir, korean] = await Promise.all([
      tmdb.trendingTv().then((p) => toCards(p.results, ContentType.TV_SHOW)),
      safeCards(() => tmdb.popularTv().then((p) => p.results), ContentType.TV_SHOW),
      safeCards(() => tmdb.topRatedTv().then((p) => p.results), ContentType.TV_SHOW),
      safeCards(() => tmdb.onAirTv().then((p) => p.results), ContentType.TV_SHOW),
      safeCards(() => tmdb.discoverTv({ originalLanguage: "ko" }).then((p) => p.results), ContentType.TV_SHOW),
    ]);
    return [
      { id: "tv_trending", title: "Trending TV", items: trending },
      { id: "tv_popular", title: "Popular Series", items: popular },
      { id: "tv_on_air", title: "On the Air", items: onAir },
      { id: "tv_top", title: "Top Rated", items: top },
      { id: "tv_korean", title: "Korean Series", items: korean },
    ].filter((r) => r.items.length > 0);
  },

  // Recent Indian OTT releases (60-day window), newest first. Future-dated
  // titles are dropped by the toCard filter. The hero is for films only, so
  // non-film releases are filtered out too: documentaries (genre 99) plus
  // stand-up/variety specials, wrestling events and other "TV special"
  // programming that flood the newest-release feed. Two pages are merged so
  // the hero still gets five real films after the filter.
  async recentOttMovies(days = 60) {
    const pages = await Promise.all([
      safeCards(() => tmdb.ottReleasesMovies({ days, page: 1 }).then((p) => p.results), ContentType.MOVIE),
      safeCards(() => tmdb.ottReleasesMovies({ days, page: 2 }).then((p) => p.results), ContentType.MOVIE),
    ]);
    return pages.flat().filter(isFeatureFilm);
  },

  // The hero's recent-OTT feed, but filtered to Indian-language films and
  // paginated deeper (pages 1–4) to fill a full home-page row: newest first,
  // de-duplicated across pages, non-film items (docs, stand-up, wrestling
  // events) dropped by the shared isFeatureFilm filter and future-dated
  // titles by the shared toCard filter.
  async recentIndianOttMovies(days = 60) {
    const pages = await Promise.all([
      safeCards(() => tmdb.ottReleasesMovies({ days, page: 1 }).then((p) => p.results), ContentType.MOVIE),
      safeCards(() => tmdb.ottReleasesMovies({ days, page: 2 }).then((p) => p.results), ContentType.MOVIE),
      safeCards(() => tmdb.ottReleasesMovies({ days, page: 3 }).then((p) => p.results), ContentType.MOVIE),
      safeCards(() => tmdb.ottReleasesMovies({ days, page: 4 }).then((p) => p.results), ContentType.MOVIE),
    ]);
    // Newest first, de-duplicated across pages, Indian films only.
    const seen = new Set();
    return pages
      .flat()
      .filter(isFeatureFilm)
      .filter((c) => {
        if (seen.has(c.tmdbId)) return false;
        seen.add(c.tmdbId);
        return INDIAN_LANGUAGES.has(c.originalLanguage);
      });
  },

  async movieDetails(id) {
    return toMovie(await tmdb.movieDetails(id));
  },

  // Full listing for one genre (Movies page "browse all" view).
  async genreMovies(genreId, page = 1) {
    const p = await tmdb.discoverMovies({ genres: String(genreId), page });
    return toCards(p.results, ContentType.MOVIE);
  },

  // Movies in the same original language as `movie` (for the "you may also
  // like" strip under the player) — the movie itself is excluded. Popular
  // first; falls back to any list we already have if the lookup fails.
  async sameLanguageMovies(movie, max = 14) {
    if (!movie || !movie.originalLanguage) return [];
    try {
      const p = await tmdb.discoverMovies({ originalLanguage: movie.originalLanguage });
      return toCards(p.results, ContentType.MOVIE)
        .filter((c) => c.tmdbId !== movie.tmdbId)
        .slice(0, max);
    } catch {
      return [];
    }
  },

  async tvDetails(id) {
    return toTvShow(await tmdb.tvDetails(id));
  },

  async seasonDetails(showId, showTitle, seasonNumber) {
    return toSeason(await tmdb.seasonDetails(showId, seasonNumber), showId, showTitle);
  },

  async search(query, languageCode = null) {
    const normalized = (query || "").trim();
    if (normalized) {
      const { phrase, terms, year } = parseSearchQuery(normalized);
      const text = phrase || normalized;
      const [page, byYear, library] = await Promise.all([
        tmdb.searchMulti(text),
        year ? searchByYear(text, year) : Promise.resolve([]),
        libraryCardsFor(terms, phrase),
      ]);
      const cards = dedupeCards([...toCards(page.results), ...byYear, ...library]);
      // The library's own titles lead their tier: they are the ones with a file.
      const libraryKeys = new Set(library.map((c) => `${c.contentType}:${c.tmdbId}`));
      // Word-aware ordering: starts-with first, then other-word matches,
      // then substrings (ties broken by rating/popularity).
      // "boys 2003" means the 2003 film, so a spelled-out year keeps only that
      // year's releases. When nothing of that name came out that year the
      // number was part of the title instead ("Blade Runner 2049"), so the same
      // cards are re-ranked with the year treated as a word.
      const ranked = rankSearchResults(cards, normalized, { libraryKeys });
      const inYear = year ? ranked.filter((c) => yearOfCard(c) === year) : ranked;
      const results = inYear.length || !year ? inYear : rankSearchResults(cards, normalized, { libraryKeys, yearIsName: true });
      return results.filter((c) => !languageCode || c.originalLanguage === languageCode);
    }
    if (languageCode) {
      const [movies, shows] = await Promise.all([
        tmdb.discoverMovies({ originalLanguage: languageCode }).then((p) => toCards(p.results, ContentType.MOVIE)),
        safeCards(() => tmdb.discoverTv({ originalLanguage: languageCode }).then((p) => p.results), ContentType.TV_SHOW),
      ]);
      return [...movies, ...shows].sort((a, b) => b.rating - a.rating);
    }
    const page = await tmdb.trending();
    return toCards(page.results);
  },
};

// Picks up to `max` slides for the hero carousel, preferring titles with
// backdrop artwork (the hero is full-bleed background imagery).
function heroSlides(movies, max = 5) {
  const withBackdrop = movies.filter((c) => c.backdropPath);
  const rest = movies.filter((c) => !c.backdropPath);
  return [...withBackdrop, ...rest].slice(0, max);
}

// Small helper shared by screens.
export function yearOf(date) {
  return date && date.length >= 4 ? date.slice(0, 4) : null;
}

// Keeps only real feature films — drops documentaries (genre 99) and the
// non-film programming that dominates recent OTT release feeds: stand-up
// specials, variety shows, wrestling/livestream events and other "TV
// special" one-offs. A generic "special" mention is the tell for most of
// them, but overviews that legitimately say "special forces/agent/ops…"
// (rare) are protected by the lookahead. Precision matters more than recall
// here: a wrongly-excluded film is simply not *featured*; a wrongly-included
// special is visible wrong content on the hero.
function isFeatureFilm(card) {
  if ((card.genreIds || []).includes(99)) return false;
  const blob = `${card.title || ""} ${card.overview || ""}`;
  if (/\bstand-?up\b|\bvariety show\b|\bwrestling\b|\bwwe\b|\bnxt\b/i.test(blob)) return false;
  if (/\bspecial\b(?!\s*(?:forces?|agents?|ops?|effects?|edition|unit|guest|appearance|report|episodes?|events?|delivery|offer|occasion|needs?|school|training|interests?))/i.test(blob)) {
    return false;
  }
  return true;
}